import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
import { Checkbox } from '../../components/ui/Checkbox';
import { extractErrorMessage } from '../../api/errors';
import { useSubscriptionPackages } from '../../queries/subscriptionPackages';
import { UserSubscription, useUpdateUserSubscription } from '../../queries/userSubscriptions';

interface EditSubscriptionDialogProps {
  subscription: UserSubscription;
  onClose: () => void;
}

// A stored date can come back as 'YYYY-MM-DD' or a full ISO timestamp; a <input
// type="date"> needs the bare date, and slicing avoids the timezone shift a
// Date round-trip would introduce.
const toDateInput = (value: string | null): string => (value ? value.slice(0, 10) : '');

// Direct edit of a subscription's own fields (package, dates, active, auto-renew),
// the manual/comp-fix path the old admin had - not the Stripe-scheduled "change
// plan". Available on any subscription so an expired/comp one can be corrected or
// re-activated. Saves via PUT /user-subscriptions/:id.
export function EditSubscriptionDialog({ subscription, onClose }: EditSubscriptionDialogProps) {
  const { data: packages = [] } = useSubscriptionPackages();
  const update = useUpdateUserSubscription(subscription.id);

  const [packageCode, setPackageCode] = useState(subscription.package_code);
  const [startDate, setStartDate] = useState(toDateInput(subscription.start_date));
  const [endDate, setEndDate] = useState(toDateInput(subscription.end_date));
  const [isActive, setIsActive] = useState(subscription.is_active);
  const [autoRenew, setAutoRenew] = useState(subscription.auto_renew);
  const [error, setError] = useState<string | null>(null);

  // The current package may be inactive/private, so include it in the options
  // even when the active-packages list wouldn't, or the select would blank it.
  const packageOptions = packages.some((p) => p.package_code === packageCode)
    ? packages
    : [...packages, { package_code: packageCode, name: packageCode } as (typeof packages)[number]];

  const handleSave = () => {
    if (!packageCode || !startDate) {
      setError('Package and start date are required.');
      return;
    }
    if (endDate && endDate < startDate) {
      setError('End date cannot be before the start date.');
      return;
    }
    setError(null);
    update.mutate(
      {
        package_code: packageCode,
        start_date: startDate,
        end_date: endDate || null,
        is_active: isActive,
        auto_renew: autoRenew,
      },
      { onSuccess: onClose, onError: (err) => setError(extractErrorMessage(err, 'Failed to update subscription.')) },
    );
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">Edit subscription</Dialog.Title>
          <p className="mt-1 text-sm text-muted-foreground">
            Edits this subscription directly. For a Stripe plan change at renewal use “Change plan” instead.
          </p>

          <div className="mt-4 space-y-3">
            <label className="block text-sm">
              Package
              <select
                className="mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm"
                value={packageCode}
                onChange={(e) => setPackageCode(e.target.value)}
              >
                {packageOptions.map((p) => (
                  <option key={p.package_code} value={p.package_code}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm">
                Start date
                <input
                  type="date"
                  className="mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </label>
              <label className="block text-sm">
                End date <span className="text-muted-foreground">(optional)</span>
                <input
                  type="date"
                  className="mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </label>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              Active
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={autoRenew} onChange={(e) => setAutoRenew(e.target.checked)} />
              Auto-renew subscription
            </label>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={update.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {update.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save changes
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
